// Creative Strategist project chat (Task #2377).
//
// Mounted ONCE at project level (outside the tab content) so the conversation
// survives tab switches: a collapsible right-hand panel on desktop, a
// bottom-sheet drawer on mobile. The thread polls only while a reply is
// pending — replies are written as a pending row the moment the message lands,
// so there is always something to show.

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import {
  AlertCircle,
  ChevronRight,
  Loader2,
  MessageSquare,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import {
  csAssetsKey,
  csChatReplyInvalidationKeys,
  csFormatDateTime,
  csMessagesKey,
  type CreativeStrategistMessage,
  type CreativeStrategistViewingContext,
} from "@/lib/creative-strategist";
import { ChatComposer, MessageAttachments } from "./chat-composer";

export function CreativeStrategistChatPanel({
  projectId,
  viewingContext,
  viewingLabel,
  open,
  onOpenChange,
  focusMessageId,
  onFocusHandled,
}: {
  projectId: string;
  viewingContext: CreativeStrategistViewingContext;
  viewingLabel: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Scroll to (and briefly highlight) one message — the Files tab back-link. */
  focusMessageId?: string | null;
  onFocusHandled?: () => void;
}) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <>
        <Button
          type="button"
          className="md:hidden fixed bottom-5 right-5 z-40 rounded-full shadow-lg h-12 w-12 p-0"
          onClick={() => onOpenChange(true)}
          data-testid="button-open-chat-mobile"
          aria-label="Open strategist chat"
        >
          <MessageSquare className="h-5 w-5" />
        </Button>
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent
            side="bottom"
            className="h-[85vh] p-0 flex flex-col gap-0"
            data-testid="sheet-chat-mobile"
          >
            {/* Radix needs a labelled dialog; the visible header is inside ChatBody. */}
            <SheetTitle className="sr-only">Strategist chat</SheetTitle>
            <SheetDescription className="sr-only">
              Talk to the Creative Strategist about this project and attach files.
            </SheetDescription>
            <ChatBody
              hideClose
              projectId={projectId}
              viewingContext={viewingContext}
              viewingLabel={viewingLabel}
              onClose={() => onOpenChange(false)}
              focusMessageId={focusMessageId}
              onFocusHandled={onFocusHandled}
            />
          </SheetContent>
        </Sheet>
      </>
    );
  }

  if (!open) {
    return (
      <div className="shrink-0 border-l bg-muted/20 w-12 sticky top-0 h-screen flex flex-col items-center pt-6">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onOpenChange(true)}
          data-testid="button-open-chat"
          aria-label="Open strategist chat"
          title="Open strategist chat"
        >
          <MessageSquare className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <aside
      className="shrink-0 border-l bg-background w-[380px] lg:w-[420px] sticky top-0 h-screen flex flex-col"
      data-testid="panel-chat-desktop"
    >
      <ChatBody
        projectId={projectId}
        viewingContext={viewingContext}
        viewingLabel={viewingLabel}
        onClose={() => onOpenChange(false)}
        focusMessageId={focusMessageId}
        onFocusHandled={onFocusHandled}
      />
    </aside>
  );
}

function ChatBody({
  projectId,
  viewingContext,
  viewingLabel,
  onClose,
  hideClose,
  focusMessageId,
  onFocusHandled,
}: {
  projectId: string;
  viewingContext: CreativeStrategistViewingContext;
  viewingLabel: string | null;
  onClose: () => void;
  /** The mobile sheet already draws its own close control. */
  hideClose?: boolean;
  focusMessageId?: string | null;
  onFocusHandled?: () => void;
}) {
  const { toast } = useToast();
  const messagesKey = csMessagesKey(projectId);
  const [targetIgnored, setTargetIgnored] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // A new view means a new default target — never carry a dismissal across it.
  const contextKey = `${viewingContext.tab ?? ""}|${viewingContext.taskId ?? ""}|${viewingContext.deliverableId ?? ""}`;
  useEffect(() => {
    setTargetIgnored(false);
  }, [contextKey]);

  const { data: messages, isLoading } = useQuery<CreativeStrategistMessage[]>({
    queryKey: [messagesKey],
    enabled: !!projectId,
    // Fast poll while the strategist owes a reply; slow poll otherwise so its
    // proactive posts (intake questions, "plan is ready — reply go") land
    // without a user turn (Task #2419).
    refetchInterval: (query) =>
      (query.state.data ?? []).some((m) => m.status === "pending") ? 2500 : 7000,
  });

  const thread = useMemo(() => messages ?? [], [messages]);
  const pendingReply = thread.some((m) => m.status === "pending");

  // When a strategist reply settles (pending → done), refresh everything the
  // reply can now change — chat drives the project, so a reply can approve the
  // plan, create decisions, and create tasks. Without this, a Tasks tab whose
  // cache holds an empty list keeps showing "No tasks yet" until a reload
  // (Task #2422).
  const prevPendingRef = useRef(false);
  useEffect(() => {
    if (prevPendingRef.current && !pendingReply) {
      for (const key of csChatReplyInvalidationKeys(projectId)) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    }
    prevPendingRef.current = pendingReply;
  }, [pendingReply, projectId]);

  // Stick to the bottom as the conversation grows.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread.length, pendingReply]);

  // Files-tab back-link: jump to the message an asset arrived with.
  useEffect(() => {
    if (!focusMessageId || thread.length === 0) return;
    const el = scrollRef.current?.querySelector(`[data-message-id="${focusMessageId}"]`);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
    setHighlightId(focusMessageId);
    onFocusHandled?.();
    const timer = setTimeout(() => setHighlightId(null), 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMessageId, thread.length]);

  const send = useMutation({
    mutationFn: async (input: { body: string; attachmentIds: string[] }) => {
      await apiRequest("POST", messagesKey, {
        body: input.body,
        attachmentIds: input.attachmentIds,
        viewingContext: targetIgnored ? null : viewingContext,
      });
    },
    onSuccess: () => {
      // The reply may still be pending at this point; the pending→settled
      // effect above re-invalidates once it lands. Invalidating here too keeps
      // the user's own turn (and anything the route did synchronously) fresh.
      for (const key of csChatReplyInvalidationKeys(projectId)) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (e: Error) => {
      toast({ title: "Message not sent", description: e.message, variant: "destructive" });
    },
  });

  const retry = useMutation({
    mutationFn: async (messageId: string) => {
      await apiRequest("POST", `${messagesKey}/${messageId}/retry`, {});
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [messagesKey] }),
    onError: (e: Error) => {
      toast({ title: "Couldn't retry", description: e.message, variant: "destructive" });
    },
  });

  return (
    <>
      <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-sm font-semibold truncate">Strategist</span>
        </div>
        {!hideClose && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="button-close-chat"
            aria-label="Close chat"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
        data-testid="list-chat-messages"
      >
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-12 w-2/3 ml-auto" />
          </div>
        ) : thread.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-10 space-y-1.5">
            <MessageSquare className="h-5 w-5 mx-auto" />
            <div>Talk to the strategist about this project.</div>
            <p className="text-xs">
              Drop in briefs, screenshots, competitor ads or spreadsheets — they become project
              files the rest of the work can use.
            </p>
          </div>
        ) : (
          thread.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              highlighted={highlightId === message.id}
              onRetry={() => retry.mutate(message.id)}
              retrying={retry.isPending}
            />
          ))
        )}
      </div>

      <ChatComposer
        projectId={projectId}
        targetLabel={viewingLabel}
        targetIgnored={targetIgnored}
        onToggleTarget={() => setTargetIgnored((v) => !v)}
        sending={send.isPending}
        onSend={async (input) => {
          await send.mutateAsync(input);
        }}
        onAssetsChanged={() =>
          queryClient.invalidateQueries({ queryKey: [csAssetsKey(projectId)] })
        }
      />
    </>
  );
}

function ChatMessage({
  message,
  highlighted,
  onRetry,
  retrying,
}: {
  message: CreativeStrategistMessage;
  highlighted: boolean;
  onRetry: () => void;
  retrying: boolean;
}) {
  const mine = message.authorRole === "user";
  return (
    <div
      className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
      data-message-id={message.id}
      data-testid={`chat-message-${message.id}`}
    >
      <div
        className={`rounded-2xl px-3.5 py-2.5 max-w-[85%] transition-shadow ${
          mine ? "bg-primary text-primary-foreground" : "bg-muted"
        } ${highlighted ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""}`}
      >
        {message.status === "pending" ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Thinking…
          </div>
        ) : message.status === "failed" ? (
          <div className="space-y-1.5">
            <div className="flex items-start gap-1.5 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
              <span>{message.errorMessage || "The strategist couldn't reply."}</span>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onRetry}
              disabled={retrying}
              data-testid={`button-retry-${message.id}`}
            >
              {retrying ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              )}
              Retry
            </Button>
          </div>
        ) : (
          <div className="text-sm whitespace-pre-wrap break-words">{message.body}</div>
        )}
      </div>
      <MessageAttachments assets={message.attachments ?? []} />
      <div className="text-[10px] text-muted-foreground mt-1">
        {csFormatDateTime(message.createdAt)}
      </div>
    </div>
  );
}
